import { useState, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { LockKey, CheckCircle, Warning } from '@phosphor-icons/react';
import { isNativeApp } from '../lib/native';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = useMemo(() => params.get('token') || '', [params]);

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const tokenMissing = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (pw !== pw2) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      toast.success('Password updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-background flex items-center justify-center px-4${isNativeApp() ? ' native-auth-screen' : ''}`}>
      <div className="noise-overlay" />
      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center space-x-3 mb-6">
            <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-white/10" data-testid="brand-logo" />
            <span className="font-heading text-2xl font-bold tracking-tight">Fan<span className="text-gold">Zingy</span></span>
          </div>
          <h1 className="font-heading text-4xl font-bold mb-2">RESET PASSWORD</h1>
          <p className="font-body text-slate-400">Choose a new password for your account</p>
        </div>

        <div className="bg-card border border-white/5 rounded-lg p-8" data-testid="reset-password-card">
          {tokenMissing ? (
            <div className="text-center space-y-3" data-testid="reset-token-missing">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-rose-500/15 border border-rose-500/40 mb-1">
                <Warning size={26} className="text-rose-400" />
              </div>
              <h2 className="font-heading text-xl font-bold">Invalid reset link</h2>
              <p className="font-body text-slate-300">This page must be opened from the reset link we sent to your email.</p>
              <Link to="/forgot-password" data-testid="goto-forgot" className="inline-block btn-secondary mt-2">Request a new link</Link>
            </div>
          ) : done ? (
            <div className="text-center space-y-3" data-testid="reset-success-state">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/40 mb-1">
                <CheckCircle size={26} className="text-emerald-400" />
              </div>
              <h2 className="font-heading text-xl font-bold">Password updated</h2>
              <p className="font-body text-slate-300">You can now sign in with your new password.</p>
              <button
                onClick={() => navigate('/login')}
                data-testid="reset-go-login-btn"
                className="btn-primary mt-2"
              >Go to login</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="reset-password-form" className="space-y-6">
              <div>
                <label className="font-body text-sm font-medium mb-2 block" htmlFor="pw">New password</label>
                <div className="relative">
                  <LockKey size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    data-testid="reset-password-input"
                    id="pw"
                    type="password"
                    required
                    minLength={6}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white placeholder:text-slate-400 pl-12 pr-4 font-body outline-none transition-colors"
                    placeholder="At least 6 characters"
                  />
                </div>
              </div>

              <div>
                <label className="font-body text-sm font-medium mb-2 block" htmlFor="pw2">Confirm new password</label>
                <div className="relative">
                  <LockKey size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    data-testid="reset-password-confirm-input"
                    id="pw2"
                    type="password"
                    required
                    minLength={6}
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    className="w-full bg-black/20 border border-white/10 focus:border-gold/50 focus:ring-1 focus:ring-gold/50 rounded-sm h-12 text-white placeholder:text-slate-400 pl-12 pr-4 font-body outline-none transition-colors"
                    placeholder="Re-enter password"
                  />
                </div>
              </div>

              <button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3.5"
              >
                {loading ? 'Updating...' : 'Update password'}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/login" data-testid="reset-back-to-login" className="font-body text-sm text-slate-400 hover:text-gold transition-colors">
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
